import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { insightsService } from '../../services/insights';
import { DailyDisconnectRow, LeagueRow, RoomDayRow } from '../../types/insights';
import { nameToColor } from '../PCTile';

// ---------------------------------------------------------------------------
// TrendsTab — 30-day room health: daily disconnect bars (room_day_report +
// insights_daily_disconnects, Test-Lab-style CSS bar rows with a per-PC split
// toggle) and the pc_league_report table.
// ---------------------------------------------------------------------------

interface TrendsTabProps {
  roomId: string;
}

const card: CSSProperties = {
  background: '#fff',
  border: '1px solid #e4e9f0',
  borderRadius: 12,
  padding: 20,
  marginBottom: 16,
};

const labelStyle: CSSProperties = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#54626f',
  marginBottom: 8,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const thTd = (head = false, left = false): CSSProperties => ({
  textAlign: left ? 'left' : 'right',
  padding: '6px 10px',
  borderBottom: '1px solid #eef1f5',
  whiteSpace: 'nowrap',
  fontVariant: 'tabular-nums',
  color: head ? '#8a97a6' : '#16202e',
  fontWeight: head ? 600 : 400,
  fontSize: head ? 11 : 12,
  textTransform: head ? 'uppercase' : 'none',
});

const fmt = (v: number | null | undefined, d = 0) => (v == null ? '—' : v.toFixed(d));

// room_day_summary is keyed on UTC days — pass UTC dates, not local ones.
const dateUtcYMD = (d: Date) => d.toISOString().slice(0, 10);

const DAYS = 30;

export default function TrendsTab({ roomId }: TrendsTabProps) {
  const [roomDays, setRoomDays] = useState<RoomDayRow[]>([]);
  const [daily, setDaily] = useState<DailyDisconnectRow[]>([]);
  const [league, setLeague] = useState<LeagueRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [split, setSplit] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const now = new Date();
    const fromD = new Date(now.getTime() - (DAYS - 1) * 86400000);
    Promise.all([
      insightsService.getRoomDays(roomId, dateUtcYMD(fromD), dateUtcYMD(now)),
      insightsService.getDailyDisconnects(roomId, DAYS),
      insightsService.getPcLeague(
        roomId,
        new Date(now.getTime() - DAYS * 86400000).toISOString(),
        now.toISOString()
      ),
    ]).then(([days, dd, lg]) => {
      if (cancelled) return;
      setRoomDays(days);
      setDaily(dd);
      setLeague(lg);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [roomId]);

  // day -> per-PC disconnect segments (split view); also totals per day.
  const byDay = useMemo(() => {
    const map = new Map<string, { total: number; marks: number; perPc: DailyDisconnectRow[] }>();
    for (const r of daily) {
      const e = map.get(r.day) ?? { total: 0, marks: 0, perPc: [] };
      e.total += r.disconnects;
      e.marks += r.operator_issues;
      e.perPc.push(r);
      map.set(r.day, e);
    }
    return map;
  }, [daily]);

  // 30 consecutive day labels (UTC days, matching the RPCs).
  const dayList = useMemo(() => {
    const out: string[] = [];
    const now = new Date();
    for (let i = DAYS - 1; i >= 0; i--) {
      const d = new Date(now.getTime() - i * 86400000);
      out.push(
        `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(
          d.getUTCDate()
        ).padStart(2, '0')}`
      );
    }
    return out;
  }, []);

  const roomDayByDay = useMemo(() => {
    const map = new Map<string, RoomDayRow>();
    for (const r of roomDays) map.set(r.day, r);
    return map;
  }, [roomDays]);

  const maxDisc = Math.max(1, ...dayList.map((d) => byDay.get(d)?.total ?? 0));
  const totals = useMemo(() => {
    let runs = 0,
      disconnects = 0,
      interrupted = 0,
      marks = 0,
      runSeconds = 0;
    for (const r of roomDays) {
      runs += r.runs ?? 0;
      disconnects += r.disconnects ?? 0;
      interrupted += r.interrupted_runs ?? 0;
      marks += r.operator_marks ?? 0;
      runSeconds += r.run_seconds ?? 0;
    }
    return { runs, disconnects, interrupted, marks, runSeconds };
  }, [roomDays]);

  if (loading) {
    return <div style={{ ...card, color: '#8a97a6' }}>Loading trends…</div>;
  }

  return (
    <>
      <div style={card}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(120px,1fr))', gap: 10, marginBottom: 18 }}>
          {[
            { k: 'Runs (30d)', v: `${totals.runs}` },
            { k: 'Play hours', v: fmt(totals.runSeconds / 3600, 1) },
            { k: 'Disconnects', v: `${totals.disconnects}` },
            {
              k: 'Interrupted runs',
              v: totals.runs > 0 ? `${((100 * totals.interrupted) / totals.runs).toFixed(1)}%` : '—',
            },
            { k: 'Operator marks', v: `${totals.marks}` },
          ].map((t) => (
            <div key={t.k} style={{ border: '1px solid #e4e9f0', borderRadius: 10, padding: '10px 12px' }}>
              <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#8a97a6', fontWeight: 600 }}>
                {t.k}
              </div>
              <div style={{ fontSize: 20, fontWeight: 600, fontVariant: 'tabular-nums' }}>{t.v}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label style={{ ...labelStyle, marginBottom: 0 }}>Daily disconnects (confirmed) · last 30 days</label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={split} onChange={(e) => setSplit(e.target.checked)} />
            Split by PC / headset
          </label>
        </div>

        <div style={{ display: 'grid', gap: 4, marginTop: 10 }}>
          {dayList.map((day) => {
            const e = byDay.get(day);
            const rd = roomDayByDay.get(day);
            const total = e?.total ?? 0;
            const marks = e?.marks ?? 0;
            return (
              <div
                key={day}
                style={{ display: 'grid', gridTemplateColumns: '78px 1fr 120px', alignItems: 'center', gap: 8, fontSize: 12 }}
              >
                <span style={{ color: '#54626f', fontVariant: 'tabular-nums' }}>{day.slice(5)}</span>
                <div style={{ background: '#eef1f5', borderRadius: 4, height: 14, display: 'flex', overflow: 'hidden' }}>
                  {split ? (
                    (e?.perPc ?? [])
                      .filter((r) => r.disconnects > 0)
                      .map((r, i) => (
                        <div
                          key={`${r.pc_name ?? 'room'}-${i}`}
                          title={`${r.pc_name ?? 'Room'}${r.hmd_mac ? ` · ${r.hmd_mac}` : ''}: ${r.disconnects}`}
                          style={{
                            width: `${(r.disconnects / maxDisc) * 100}%`,
                            background: nameToColor(r.pc_name ?? 'room').border,
                            height: '100%',
                            minWidth: r.disconnects > 0 ? 2 : 0,
                          }}
                        />
                      ))
                  ) : (
                    <div
                      style={{
                        width: `${(total / maxDisc) * 100}%`,
                        background: '#c0392b',
                        height: '100%',
                        borderRadius: 4,
                        minWidth: total > 0 ? 2 : 0,
                      }}
                    />
                  )}
                </div>
                <span style={{ textAlign: 'right', fontVariant: 'tabular-nums', color: '#54626f' }}>
                  {total} disc{marks > 0 ? ` · ${marks} marks` : ''}
                  {rd?.interrupted_run_pct != null ? ` · ${rd.interrupted_run_pct.toFixed(0)}% intr` : ''}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div style={card}>
        <label style={labelStyle}>PC / headset league · last 30 days</label>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                {['PC', 'Headset', 'runs', 'play h', 'gaps', 'disc', 'disc/h', 'deauths', 'rssi', 'rssi p5', 'fps p1', 'bitrate', 'tx max', 'worst scene'].map(
                  (h, i) => (
                    <th key={h} style={thTd(true, i <= 1 || i === 13)}>
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {league.map((r) => (
                <tr key={r.pc_instance_id}>
                  <td style={thTd(false, true)}>{r.pc_name}</td>
                  <td style={thTd(false, true)} title={r.hmd_mac ?? undefined}>
                    {r.hmd_label ?? r.hmd_mac ?? '—'}
                  </td>
                  <td style={thTd()}>{r.runs}</td>
                  <td style={thTd()}>{fmt(r.play_hours, 1)}</td>
                  <td style={thTd()}>{r.gaps}</td>
                  <td style={{ ...thTd(), color: r.disconnects > 0 ? '#c0392b' : '#16202e', fontWeight: r.disconnects > 0 ? 600 : 400 }}>
                    {r.disconnects}
                  </td>
                  <td style={thTd()}>{fmt(r.disconnects_per_hour, 2)}</td>
                  <td style={thTd()}>{r.deauths}</td>
                  <td style={thTd()}>{fmt(r.rssi_avg, 0)}</td>
                  <td style={thTd()}>{fmt(r.rssi_p5, 0)}</td>
                  <td style={thTd()}>{fmt(r.fps_p1_avg, 1)}</td>
                  <td style={thTd()}>{fmt(r.bitrate_avg, 0)}</td>
                  <td style={thTd()}>{fmt(r.transmit_max, 0)}</td>
                  <td style={thTd(false, true)}>{r.worst_scene ?? '—'}</td>
                </tr>
              ))}
              {league.length === 0 && (
                <tr>
                  <td colSpan={14} style={{ ...thTd(false, true), color: '#8a97a6' }}>
                    No data for this room in the last 30 days.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div style={{ fontSize: 11, color: '#8a97a6', marginTop: 8 }}>
          RSSI columns read raw router station samples (14-day retention) — blank for older windows or
          before the router poller is live.
        </div>
      </div>
    </>
  );
}
